import React, { useContext, useEffect, useState } from "react";
import { Tr, Td, Flex, Switch, Text, Box } from "@chakra-ui/react";
import { useClasswizeToast } from "@/components/ui/Toast";
import { colours } from "@/utils/themeUtils";
import { ScheduleDayOfWeek, ScheduleResponse } from "@/api/schedules";
import { formatDateAsLocaleDateString, format24HrTimeInputAs12Hr, getDateFromDDMMYYYY, isDateInThePast } from "@/utils/dateTimeUtils";
import { ClasswizeSelect } from "../ui/Select";
import { TimeInput24Hr, TimeInput } from "../TimeInput";
import { DatePickerInput } from "../DatePickerInput";
import {
	useCreateScheduleMutation,
	useDeleteScheduleMutation,
	useToggleScheduleActiveMutation,
	useUpdateScheduleMutation,
} from "@/query/scheduleQuery";
import { OptionButton } from "./OptionButton";
import { ScheduleTableContext } from ".";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { UNEXPECTED_ERROR } from "@/utils/envUtils";
import {
	captureScheduleCreationOrUpdateEvent,
	captureScheduleDeletionEvent,
	captureScheduleDuplicateEvent,
	captureScheduleToggleEvent,
} from "@/utils/posthogUtils";

export type ScheduleRule = {
	id: number;
	day: ScheduleDayOfWeek;
	startTime: TimeInput24Hr;
	endTime: TimeInput24Hr;
	endDate: Date;
	active: boolean;
	modified: {
		by: string;
		date: string;
	};
};

interface Props {
	classroomId: string;
	/** The row will start in edit mode if this is true */
	isNewSchedule: boolean;
	/** Undefined for new rules that are not a duplicated rule */
	schedule?: ScheduleRule;
	className?: string;
}

const dayLabels: Record<ScheduleResponse["day"], string> = {
	[ScheduleDayOfWeek.Monday]: "Monday",
	[ScheduleDayOfWeek.Tuesday]: "Tuesday",
	[ScheduleDayOfWeek.Wednesday]: "Wednesday",
	[ScheduleDayOfWeek.Thursday]: "Thursday",
	[ScheduleDayOfWeek.Friday]: "Friday",
	[ScheduleDayOfWeek.Saturday]: "Saturday",
	[ScheduleDayOfWeek.Sunday]: "Sunday",
};

const dayOptions = Object.values(ScheduleDayOfWeek).map((day) => ({ label: dayLabels[day], value: day }));

function ErrorMessage(props: { error?: string }) {
	return (
		<Box mt={1} height={"16px"}>
			<Text color="#CB4655" fontSize={"sm"}>
				{props.error}
			</Text>
		</Box>
	);
}

export function ScheduleTableRow(props: Props): JSX.Element {
	const { setShowNewScheduleRow, handleNewScheduleRule, registerRowInEditMode, unregisterRowInEditMode, exceedsStudentLimit } =
		useContext(ScheduleTableContext);
	const useToast = useClasswizeToast();

	function successToast(title: string) {
		useToast.closeAll();
		useToast.successToast({
			title,
			isClosable: true,
		});
	}

	function errorToast(title: string, description: string) {
		useToast.closeAll();
		useToast.errorToast({
			title,
			description,
			isClosable: true,
		});
	}

	/**
	 * A single function for the multiple cases we need to handle an error when creating, updating, or activating a schedule.
	 */
	function handleScheduleChangeError(error: Error, action: "create" | "update" | "activate") {
		if (error.message.includes("schedule overlap detected")) {
			errorToast(`Could not ${action} schedule`, "The schedule overlaps with an existing schedule.");
			return;
		}
		if (error.message.includes("end date cannot be more than 1 year in the future")) {
			errorToast(`Could not ${action} schedule`, "The end date cannot be more than one year in the future.");
			return;
		}
		if (error.message.includes("end date cannot be in the past")) {
			errorToast(`Could not ${action} schedule`, "The end date cannot be in the past.");
			return;
		}
		if (error.message.includes("outside of school hours")) {
			errorToast(`Could not ${action} schedule`, "The schedule is outside of school hours.");
			return;
		}
		errorToast(`Error ${action.replace("e", "ing")} class schedule`, UNEXPECTED_ERROR);
	}

	const userSessionStore = useUserSessionStore();
	const classroomName = props.className || "Unknown class";

	const createScheduleMutation = useCreateScheduleMutation(props.classroomId, userSessionStore.getActiveUser() ?? "", {
		onSuccess: () => {
			successToast("Class schedule saved successfully");
			// Only close the new schedule row and exit edit mode on successful creation
			setShowNewScheduleRow(false);
			unregisterRowInEditMode(props.schedule?.id ?? 0);

			// Capture PostHog event for successful schedule creation
			if (day && startTime && endTime && endDate) {
				captureScheduleCreationOrUpdateEvent(
					false, // isUpdate
					dayLabels[day],
					startTime,
					endTime,
					endDate,
					scheduleActive,
					userSessionStore.getActiveUser() as string,
					userSessionStore.getActiveDevice() as string,
					classroomName
				);
			}
		},
		onError: (error) => {
			handleScheduleChangeError(error, "create");
		},
	});
	const updateScheduleMutation = useUpdateScheduleMutation(props.classroomId, userSessionStore.getActiveUser() ?? "", {
		onSuccess: () => {
			successToast("Class schedule saved successfully");
			setInEditMode(false);
			unregisterRowInEditMode(props.schedule?.id ?? 0);
			// Capture PostHog event for successful schedule update
			if (day && startTime && endTime && endDate) {
				captureScheduleCreationOrUpdateEvent(
					true, // isUpdate
					dayLabels[day],
					startTime,
					endTime,
					endDate,
					scheduleActive,
					userSessionStore.getActiveUser() as string,
					userSessionStore.getActiveDevice() as string,
					classroomName
				);
			}
		},
		onError: (error) => {
			handleScheduleChangeError(error, "update");
		},
	});
	const deleteScheduleMutation = useDeleteScheduleMutation(props.classroomId, userSessionStore.getActiveUser() ?? "", {
		onSuccess: () => {
			// Capture PostHog event for successful schedule deletion
			if (props.schedule) {
				captureScheduleDeletionEvent(
					dayLabels[props.schedule.day],
					props.schedule.startTime,
					props.schedule.endTime,
					props.schedule.endDate,
					props.schedule.active,
					userSessionStore.getActiveUser() as string,
					userSessionStore.getActiveDevice() as string,
					classroomName
				);
			}
			successToast("Class schedule has been deleted");
		},
		onError: () => {
			errorToast("Error deleting class schedule", UNEXPECTED_ERROR);
		},
	});
	const toggleScheduleActiveMutation = useToggleScheduleActiveMutation(props.classroomId, userSessionStore.getActiveUser() ?? "", {
		onSuccess: () => {
			successToast("Class schedule saved successfully");
			// Capture PostHog event for successful schedule toggle
			if (props.schedule) {
				captureScheduleToggleEvent(
					scheduleActive,
					dayLabels[props.schedule.day],
					props.schedule.startTime,
					props.schedule.endTime,
					props.schedule.endDate,
					userSessionStore.getActiveUser() as string,
					userSessionStore.getActiveDevice() as string,
					classroomName
				);
			}
		},
		onError: (error) => {
			handleScheduleChangeError(error, "activate");
		},
	});

	// Used to determine whether to update the schedule or create a new one, and whether to show the "last updated" information
	const isNewDuplicatedSchedule = props.isNewSchedule && !!props.schedule;

	const [scheduleActive, setScheduleActive] = useState(props.schedule?.active ?? false);

	const [inEditMode, setInEditMode] = useState(props.isNewSchedule);

	const [day, setDay] = useState<ScheduleDayOfWeek | undefined>(props.schedule?.day);
	const [dayError, setDayError] = useState<string | undefined>();

	const [startTime, setStartTime] = useState<TimeInput24Hr | undefined>(props.schedule?.startTime);
	const [startTimeError, setStartTimeError] = useState<string | undefined>();

	const [endTime, setEndTime] = useState<TimeInput24Hr | undefined>(props.schedule?.endTime);
	const [endTimeError, setEndTimeError] = useState<string | undefined>();

	const [endDate, setEndDate] = useState<Date | null>(props.schedule?.endDate ?? null);
	const [endDateError, setEndDateError] = useState<string | undefined>();

	const [hasValidationErrors, setHasValidationErrors] = useState(false);

	function validateDay(dayToValidate?: ScheduleDayOfWeek): boolean {
		const dayValue = dayToValidate ?? day;
		if (!dayValue) {
			setDayError("Day is required");
			return false;
		}
		setDayError(undefined);
		return true;
	}

	function validateStartTime(startTimeToValidate?: TimeInput24Hr): boolean {
		const startTimeValue = startTimeToValidate ?? startTime;
		if (!startTimeValue) {
			setStartTimeError("Start time is required");
			return false;
		}
		setStartTimeError(undefined);
		return true;
	}

	function validateEndTime(endTimeToValidate?: TimeInput24Hr, startTimeToValidate?: TimeInput24Hr): boolean {
		const endTimeValue = endTimeToValidate ?? endTime;
		const startTimeValue = startTimeToValidate ?? startTime;

		if (!endTimeValue) {
			setEndTimeError("End time is required");
			return false;
		}
		if (
			startTimeValue &&
			(endTimeValue.hour < startTimeValue.hour ||
				(endTimeValue.hour === startTimeValue.hour && endTimeValue.minute <= startTimeValue.minute))
		) {
			setEndTimeError("End time must be after start time");
			return false;
		}
		setEndTimeError(undefined);
		return true;
	}

	function validateEndDate(endDateToValidate?: Date): boolean {
		const endDateValue = endDateToValidate ?? endDate;
		if (!endDateValue) {
			setEndDateError("End date is required");
			return false;
		}
		// Compare dates only, not datetime to avoid timezone issues
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const compareDate = new Date(endDateValue);
		compareDate.setHours(0, 0, 0, 0);

		if (compareDate < today) {
			setEndDateError("Cannot be in the past");
			return false;
		}

		// Check if end date is more than one year in the future
		const oneYearFromToday = new Date(today);
		oneYearFromToday.setFullYear(oneYearFromToday.getFullYear() + 1);

		if (compareDate > oneYearFromToday) {
			setEndDateError("Cannot be more than one year in the future");
			return false;
		}

		setEndDateError(undefined);
		return true;
	}

	function validateSchedule(): boolean {
		if (!validateDay()) return false;
		if (!validateStartTime()) return false;
		if (!validateEndTime()) return false;
		if (!validateEndDate()) return false;
		return true;
	}

	// Sync state when schedule changes
	useEffect(() => {
		if (props.schedule) {
			setDay(props.schedule.day);
			setStartTime(props.schedule.startTime);
			setEndTime(props.schedule.endTime);
			setEndDate(props.schedule.endDate);
			setScheduleActive(props.schedule.active);
		}
	}, [props.schedule]);

	useEffect(() => {
		setHasValidationErrors(!!dayError || !!startTimeError || !!endTimeError || !!endDateError);
	}, [dayError, startTimeError, endTimeError, endDateError]);

	function handleDeleteSchedule(scheduleId: number) {
		deleteScheduleMutation.mutate(scheduleId);
	}

	function handleDuplicateSchedule(schedule: ScheduleRule) {
		// Capture PostHog event for schedule duplication
		captureScheduleDuplicateEvent(userSessionStore.getActiveUser() as string, userSessionStore.getActiveDevice() as string, classroomName);

		handleNewScheduleRule(schedule);
	}

	function handleToggleEnable(e: React.ChangeEvent<HTMLInputElement>) {
		setScheduleActive(e.target.checked);

		if (inEditMode) return;

		toggleScheduleActiveMutation.mutate({
			scheduleId: props.schedule?.id as number,
			active: e.target.checked,
		});
	}

	function saveChanges() {
		if (!validateSchedule()) {
			return;
		}

		// Validation passed, so these values should be defined
		if (!day || !startTime || !endTime || !endDate) {
			console.error("Validation passed but required fields are missing");
			return;
		}

		if (!isNewDuplicatedSchedule && props.schedule?.id) {
			updateScheduleMutation.mutate({
				id: props.schedule.id,
				day,
				startTime,
				endTime,
				endDate,
				active: scheduleActive,
			});
		} else {
			createScheduleMutation.mutate({
				day,
				startTime,
				endTime,
				endDate,
				active: scheduleActive,
			});
		}

		// Note: For new schedules, the row closing is handled in the mutation success callback to ensure the row stays open if creation fails
	}

	function cancelChanges() {
		if (props.isNewSchedule) {
			setDay(undefined);
			setStartTime(undefined);
			setEndTime(undefined);
			setEndDate(null);
			setScheduleActive(false);
			setDayError(undefined);
			setStartTimeError(undefined);
			setEndTimeError(undefined);
			setEndDateError(undefined);
			unregisterRowInEditMode(props.schedule?.id ?? 0);

			setShowNewScheduleRow(false);
			return;
		}

		// Revert to original values from props
		setDay(props.schedule?.day);
		setStartTime(props.schedule?.startTime);
		setEndTime(props.schedule?.endTime);
		setEndDate(props.schedule?.endDate ?? null);
		setScheduleActive(props.schedule?.active ?? false);

		// Clear any validation errors
		setDayError(undefined);
		setStartTimeError(undefined);
		setEndTimeError(undefined);
		setEndDateError(undefined);

		setInEditMode(false);
		unregisterRowInEditMode(props.schedule?.id ?? 0);
	}

	return (
		<>
			<Tr
				key={props.schedule?.id}
				backgroundColor={hasValidationErrors ? "#FEE4E299 !important" : inEditMode ? "#DDFBF799 !important" : "transparent"}
			>
				<Td px={4}>
					{inEditMode ? (
						<>
							<ClasswizeSelect
								width="120px"
								value={day}
								onChange={(value) => {
									setDay(value as ScheduleDayOfWeek);
									validateDay(value as ScheduleDayOfWeek);
								}}
								placeholder="Select day"
								options={dayOptions}
							/>
							{hasValidationErrors && <ErrorMessage error={dayError} />}
						</>
					) : (
						dayLabels[day ?? ScheduleDayOfWeek.Monday]
					)}
				</Td>
				<Td px={4}>
					{inEditMode ? (
						<>
							<TimeInput
								value={startTime}
								handleChange={(value) => {
									setStartTime(value);
									validateStartTime(value);
									// Re-validate end time since it depends on start time
									if (endTime) {
										validateEndTime(endTime, value);
									}
								}}
							/>
							{hasValidationErrors && <ErrorMessage error={startTimeError} />}
						</>
					) : (
						format24HrTimeInputAs12Hr(props.schedule?.startTime ?? { hour: 0, minute: 0 })
					)}
				</Td>
				<Td px={4}>
					{inEditMode ? (
						<>
							<TimeInput
								value={endTime}
								handleChange={(value) => {
									setEndTime(value);
									validateEndTime(value, startTime);
								}}
							/>
							{hasValidationErrors && <ErrorMessage error={endTimeError} />}
						</>
					) : (
						format24HrTimeInputAs12Hr(props.schedule?.endTime ?? { hour: 0, minute: 0 })
					)}
				</Td>
				<Td px={4} color={isDateInThePast(endDate as Date) ? colours.danger.icon : "inherit"}>
					{inEditMode ? (
						<>
							<DatePickerInput
								value={endDate}
								width="140px"
								onDateChange={(date) => {
									if (date) {
										setEndDate(date);
										validateEndDate(date);
									}
								}}
								isInvalid={hasValidationErrors || (!!endDate && isDateInThePast(endDate))}
							/>
							{hasValidationErrors && <ErrorMessage error={endDateError} />}
						</>
					) : (
						formatDateAsLocaleDateString(endDate as Date)
					)}
				</Td>
				<Td px={2} textAlign="center">
					<Switch
						isChecked={scheduleActive}
						onChange={handleToggleEnable}
						isDisabled={toggleScheduleActiveMutation.isLoading || exceedsStudentLimit}
					/>
					{hasValidationErrors && <ErrorMessage />}
				</Td>
				<Td px={4}>
					{inEditMode ? (
						<Flex key="edit-mode-buttons" gap={2} justifyContent="center">
							<OptionButton
								iconName="fa-check"
								label="Save"
								onClick={saveChanges}
								isDisabled={hasValidationErrors || createScheduleMutation.isLoading || updateScheduleMutation.isLoading}
							/>
							<OptionButton
								iconName="fa-xmark"
								label="Cancel"
								onClick={cancelChanges}
								isDisabled={createScheduleMutation.isLoading || updateScheduleMutation.isLoading}
							/>
						</Flex>
					) : (
						<Flex key="view-mode-buttons" gap={2} justifyContent="center">
							<OptionButton
								iconName="fa-pencil"
								label="Edit"
								onClick={() => {
									setInEditMode(true);
									registerRowInEditMode(props.schedule?.id ?? 0);
								}}
								isDisabled={
									hasValidationErrors || deleteScheduleMutation.isLoading || toggleScheduleActiveMutation.isLoading || exceedsStudentLimit
								}
							/>
							<OptionButton
								iconName="fa-copy"
								label="Duplicate"
								onClick={() => handleDuplicateSchedule(props.schedule as ScheduleRule)}
								isDisabled={
									hasValidationErrors || deleteScheduleMutation.isLoading || toggleScheduleActiveMutation.isLoading || exceedsStudentLimit
								}
							/>
							<OptionButton
								iconName="fa-trash"
								label="Delete"
								onClick={() => handleDeleteSchedule(props.schedule?.id ?? 0)}
								isDisabled={hasValidationErrors || deleteScheduleMutation.isLoading || toggleScheduleActiveMutation.isLoading}
							/>
						</Flex>
					)}
					{hasValidationErrors && <ErrorMessage />}
				</Td>
			</Tr>
			{!isNewDuplicatedSchedule && props.schedule?.modified && (
				<>
					{/* The additional hidden row is required to ensure the striping of the table is visually maintained */}
					<Tr hidden></Tr>
					<Tr backgroundColor={hasValidationErrors ? "#FEE4E299 !important" : inEditMode ? "#DDFBF799 !important" : "transparent"}>
						<Td colSpan={6} pt={0} pb={2} px={4}>
							<Text fontSize="sm" color={colours.gray[700]} mt={-2} mb={2}>
								Last updated: {props.schedule?.modified.by} on{" "}
								{formatDateAsLocaleDateString(getDateFromDDMMYYYY(props.schedule?.modified.date as string))}
							</Text>
						</Td>
					</Tr>
				</>
			)}
		</>
	);
}
